import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Round, RoundItem } from '../data/types';
import { isReal } from '../data/types';
import { useRoundProgress } from './useRoundProgress';
import { usePreloadRound } from './usePreloadRound';
import { headlineScaleStyle } from './headlineScale';
import { useSessionRecord, type SessionSource } from './useSessionRecord';
import RealReveal from './RealReveal';
import FakeReveal from './FakeReveal';
import '../styles/play.css';

interface Props {
  round: Round;
  /** Test seam for opening the article tab. */
  openArticle?: (url: string) => Window | null;
  /* Preview mode: jump straight to one item. A FAKE item opens its page immediately — that
     page IS the thing being reviewed. A REAL item stops on the headline with REVEAL ready
     rather than auto-opening the article: without a click there is no user gesture, so the
     browser would block the tab and the reviewer would just see the fallback. */
  focusSlug?: string;
  /* Endless Mode: the run has no planned length, so the counter loses its total and the
     progress bar goes away entirely. Everything else — reveal, Back, hash, bfcache, keyboard —
     is identical, because none of it ever depended on knowing how many headlines there were. */
  endless?: boolean;
  /* Records that this series was played. Omitted entirely for the static demo, which is nobody's
     recording. Never allowed to affect what is on screen — see useSessionRecord. */
  session?: { source: SessionSource; key: string };
}

/* How far ahead Endless caches graphics before enabling START. The pool can be hundreds of
   headlines and he may never reach most of them; waiting on all of it would be a loading bar
   instead of a shoot. */
export const ENDLESS_PRELOAD_AHEAD = 20;

export default function PlayView({ round, openArticle, focusSlug, endless = false, session }: Props) {
  const items = round.items;
  const count = items.length;
  const progress = useRoundProgress(count);
  const preload = usePreloadRound(round, endless ? ENDLESS_PRELOAD_AHEAD : undefined);

  const record = useSessionRecord(session?.source ?? null, session?.key ?? '', items.length);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [reveal, setReveal] = useState<RoundItem | null>(null);
  const revealRef = useRef<RoundItem | null>(null);
  revealRef.current = reveal;

  const item: RoundItem | undefined = items[progress.index];

  // Jump to the requested item once, as soon as the round is cached.
  const jumped = useRef(false);
  useEffect(() => {
    if (jumped.current || !focusSlug || !preload.ready) return;
    const target = items.findIndex((i) => i.slug === focusSlug || i.id === focusSlug);
    if (target === -1) return;
    jumped.current = true;
    progress.goTo(target);
    setStarted(true);
    if (!isReal(items[target])) setReveal(items[target]);
  }, [focusSlug, items, preload.ready, progress]);

  const closeReveal = useCallback(() => {
    // Unwind the history entry the reveal pushed, so Back and Esc leave history in the
    // same shape. popstate then clears the overlay.
    if (revealRef.current) window.history.back();
  }, []);

  const openReveal = useCallback(
    (target: RoundItem) => {
      if (!target) return;
      // Order matters. The verdict must be in the URL before we push, so the entry we come
      // BACK to already carries it.
      progress.markRevealed(target.verdict);
      window.history.pushState({ rofReveal: target.id }, '', window.location.href);
      setReveal(target);
    },
    [progress],
  );

  /* Back out of a reveal. Also fires when Back is pressed on the REAL interstitial before it
     has navigated. Clearing `reveal` unmounts RealReveal, whose cleanup kills the pending
     navigation timer — otherwise the browser would jump to the article unprompted. */
  useEffect(() => {
    const onPop = () => setReveal(null);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  /* Kept even though the real reveal now opens a new tab instead of navigating away: a
     refresh, a restored session, or Back out of a fake reveal can all hand this page back from
     bfcache mid-reveal, with the REVEAL button disabled. In the prototype that left the button
     frozen on "Loading…" and it reached a recording. Re-read the hash and drop any reveal
     state whenever the browser hands the page back. */
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      setReveal(null);
      setFinished(false);
      progress.resync();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [progress]);

  /* Editing the hash on an already-open tab is a same-document navigation: the browser fires
     hashchange and does NOT reload. Without this, pasting a resume link like #4-rrrr...... into
     a tab that already has the round open silently does nothing — it looks like the link is
     broken. (Our own replaceState calls also fire this; resync is idempotent, so re-reading
     the hash we just wrote is a no-op.) */
  useEffect(() => {
    const onHashChange = () => progress.resync();
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [progress]);

  const next = useCallback(() => {
    if (progress.index >= count - 1) {
      setFinished(true);
      return;
    }
    progress.goTo(progress.index + 1);
  }, [count, progress]);

  const prev = useCallback(() => {
    if (finished) {
      setFinished(false);
      return;
    }
    progress.goTo(progress.index - 1);
  }, [finished, progress]);

  // Keyboard: Space reveals, arrows move. Blur first so a focused nav button doesn't fire
  // twice off one Space press — that bug shipped in the prototype.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (reveal) return;
      const active = document.activeElement as HTMLElement | null;
      if (active && active.tagName === 'BUTTON') active.blur();

      if (!started) {
        if (e.code === 'Space' || e.key === 'Enter') {
          e.preventDefault();
          if (preload.ready) setStarted(true);
        }
        return;
      }
      if (e.code === 'Space') {
        e.preventDefault();
        if (!finished && item && !progress.flags[progress.index]) openReveal(item);
        return;
      }
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [finished, item, next, openReveal, preload.ready, prev, progress, reveal, started]);

  /* Report progress as it changes — deduped in the hook, fire-and-forget, and it cannot reach
     React state, so it can never rerender the play surface mid-take. `remember` keeps the
     latest for the pagehide top-up. */
  const snapshot = useMemo(() => ({
    reached: progress.index,
    // What he actually played. Stepping past a headline is not the same as revealing it.
    revealed: progress.realCount + progress.fakeCount,
    realCount: progress.realCount,
    fakeCount: progress.fakeCount,
    total: count,
    finished,
  }), [progress.index, progress.realCount, progress.fakeCount, count, finished]);

  useEffect(() => {
    if (!started) return;
    record.remember(snapshot);
    record.update(snapshot);
  }, [started, snapshot, record]);

  if (reveal) {
    return isReal(reveal) ? (
      <RealReveal
        item={reveal}
        headlineSize={round.headlineSize}
        stampUrl={round.stampRealUrl}
        openArticle={openArticle}
        onOpened={closeReveal}
        onCancel={closeReveal}
      />
    ) : (
      <FakeReveal
        item={reveal}
        ribbonLabel={round.ribbonLabel || ''}
        headlineSize={round.headlineSize}
        showWordmark={round.showWordmark !== false}
        stampUrl={round.stampFakeUrl}
        onClose={closeReveal}
      />
    );
  }

  if (!started) {
    return (
      <div className="centred" data-testid="ready-gate">
        {/* The show's name, never the round's. Round titles are working names — "Round 1 - 8/10"
            — and are for the builder only; they must never reach a recording. */}
        <div className="round-title">Real or Fake?</div>
        {endless && <div className="kicker">ENDLESS</div>}
        <div className="big">{preload.ready ? 'Ready' : 'Loading series…'}</div>
        {preload.ready ? (
          <>
            {/* Only surfaced when something is actually wrong — the happy path says nothing,
                because a wall of reassurance is just clutter on the screen before a take. */}
            {preload.failed.length > 0 && (
              <div className="sub">
                {preload.failed.length} graphic{preload.failed.length === 1 ? '' : 's'} could not be
                loaded and will show a placeholder.
              </div>
            )}
            <button className="reveal-btn" onClick={() => { record.begin(); setStarted(true); }} data-testid="start-btn">
              START
            </button>
            <div className="hint">or press Space</div>
          </>
        ) : (
          <>
            <div className="preload-track">
              <div
                className="preload-fill"
                style={{ width: `${preload.total ? (preload.loaded / preload.total) * 100 : 0}%` }}
              />
            </div>
            <div className="sub">
              Caching {preload.total} image{preload.total === 1 ? '' : 's'} before the series starts.
            </div>
          </>
        )}
      </div>
    );
  }

  const revealed = progress.flags[progress.index];

  return (
    /* Scaled from the CURRENT item, so the pre-reveal headline auto-fits per headline rather
       than per round — a short one is not shrunk just because a long one shares the round. */
    <div className="play" style={headlineScaleStyle(round.headlineSize, item?.headline || '')}>
      <header className="play-header">
        <div className="wordmark">Adorian</div>
        <div className="tally">
          <span className="tally-real">{progress.realCount} REAL</span>
          <span className="tally-fake">{progress.fakeCount} FAKE</span>
        </div>
      </header>

      {/* No bar in Endless: there is no end to fill toward, and a bar that creeps to 100% would
          promise one. */}
      {!endless && (
        <div className="progress-track">
          <div
            className="progress-fill"
            style={{ width: `${finished ? 100 : (progress.index / Math.max(count - 1, 1)) * 100}%` }}
          />
        </div>
      )}

      <main className="play-main">
        {finished ? (
          <>
            {/* Running the endless pool dry is a normal ending, not a failure — same wrap
                screen, so it never reads like something broke. */}
            <div className="kicker">{endless ? 'THAT IS EVERY HEADLINE' : 'SERIES COMPLETE'}</div>
            <div className="done-screen">
              <div className="big">That&rsquo;s a wrap!</div>
              <div className="revealed-tag">
                {progress.realCount + progress.fakeCount} of {count} headlines revealed ·{' '}
                {progress.realCount} real · {progress.fakeCount} fake
              </div>
            </div>
          </>
        ) : (
          <>
            {/* No count on any card, in either mode. It used to read "HEADLINE 01 OF 15" in a
                series, and a climbing number invites "how many left?" — which is a question
                about the format, not about the headline, and it is on screen while he is
                deciding. Endless dropped it first; series followed 2026-08-21 at Lucky's ask.

                ⚠️ The position itself is NOT gone, only its display: play progress still lives
                in the URL hash as `#<index>-<flags>`, which is what survives the navigation
                every reveal performs. Nothing about resuming changed. */}
            <div className="kicker">HEADLINE</div>
            <div className="headline">{item?.headline}</div>
            <div className="verdict-wrap">
              {revealed ? (
                <div className={`revealed-tag ${revealed}`}>
                  {revealed === 'real' ? 'REVEALED — REAL ✓' : 'REVEALED — FAKE ✕'} · use PREV/NEXT to move on
                </div>
              ) : (
                <button
                  className="reveal-btn"
                  onClick={() => item && openReveal(item)}
                  data-testid="reveal-btn"
                >
                  REVEAL VERDICT
                </button>
              )}
            </div>
          </>
        )}
      </main>

      <nav className="play-controls">
        <button className="nav-btn" onClick={prev} disabled={progress.index === 0 && !finished}>
          ◀ PREV
        </button>
        <div className="hint">Space = reveal · → = next · ← = prev · articles open in a new tab</div>
        <button className="nav-btn" onClick={next} disabled={finished}>
          {progress.index === count - 1 ? 'FINISH ▶' : 'NEXT ▶'}
        </button>
      </nav>
    </div>
  );
}
